import React, { useState, useEffect, useCallback } from 'react';
import { Editor, Frame, Element, useEditor } from '@craftjs/core';
import { makeStyles } from '@material-ui/core/styles';
import {
  Grid, Paper, Box, AppBar, Toolbar, Typography,
  Button, IconButton, Snackbar, Dialog, DialogContent, DialogTitle,
  DialogActions, TextField, CircularProgress, Chip,
  FormControl, InputLabel, Select, MenuItem, FormHelperText,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import * as Icons from '@material-ui/icons';
import { useHistory, useParams } from 'react-router-dom';
import moment from 'moment';

import { Text }                         from '../components/Text';
import { Button as CustomButton }       from '../components/Button';
import { Container }                    from '../components/Container';
import { Image }                        from '../components/Image';
import { Toolbox }                      from '../components/Toolbox';
import { SettingsPanel }                from '../components/Settingspanel';
import { Input }                        from '../components/Input';
import { Form }                         from '../components/Form';
import { Grid as CustomGrid }           from '../components/Grid';
import { Card }                         from '../components/Card';
import { Divider }                      from '../components/Divider';
import { HtmlBlock }                    from '../components/HtmlBlock';
import { useDesignManager }             from '../hooks/useDesignManager';
import DesignListDialog                 from '../components/DesignListDialog';
import { StylesBlock } from '../components/StylesBlock';
import { Section }     from '../components/Section';
import { ChannelingCard }       from '../components/ChannelingCard';
import { SearchBox }             from '../components/SearchBox';
import { ChannelingDetailBlock } from '../components/ChannelingDetailBlock';
import { DynamicField }          from '../components/DynamicField';
import { CONTENT_TYPES }         from '../config/contentTypes';
import MenuEditorDialog           from '../components/MenuEditorDialog';

// ---------------------------------------------------------------------------
// Estilos
// ---------------------------------------------------------------------------
const useStyles = makeStyles((theme) => ({
  root:         { height: 'calc(100vh - 64px)', overflow: 'hidden' },
  toolbar: {
    backgroundColor: '#fff',
    color: '#222',
    borderBottom: '1px solid #e0e0e0',
  },
  leftPanel:    { height: 'calc(100vh - 128px)', overflow: 'hidden' },
  rightPanel:   { height: 'calc(100vh - 128px)', overflow: 'hidden' },
  canvas: {
    height: 'calc(100vh - 128px)',
    overflow: 'auto',
    backgroundColor: '#f5f5f5',
    padding: theme.spacing(2),
  },
  frame:          { width: '100%', minHeight: '600px', backgroundColor: '#fff' },
  actionButton:   { marginLeft: theme.spacing(1) },
  title:          { flexGrow: 1 },
  previewContent: { minHeight: '500px', backgroundColor: '#f5f5f5', padding: theme.spacing(2) },
  statusChip: {
    position: 'fixed',
    bottom: theme.spacing(2),
    right: theme.spacing(2),
    zIndex: 1000,
  },
  loadingContainer: {
    height: '100vh',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 16,
  },
}));

// ---------------------------------------------------------------------------
// Resolver — registra todos los componentes CraftJS
// HtmlBlock se mantiene para compatibilidad con diseños ya guardados en BD
// ---------------------------------------------------------------------------
const RESOLVER = {
  Text,
  Button: CustomButton,
  Container,
  Image,
  Input,
  Form,
  Grid: CustomGrid,
  Card,
  Divider,
  HtmlBlock,
  StylesBlock,
  Section,
  ChannelingCard,
  SearchBox,
  ChannelingDetailBlock,
  DynamicField,
};

// ---------------------------------------------------------------------------
// sanitizeDesign
// Corrige nodos huérfanos, referencias rotas y isCanvas incorrecto.
// ---------------------------------------------------------------------------
function sanitizeDesign(design) {
  if (!design) return null;

  let obj = design;
  if (typeof design === 'string') {
    try { obj = JSON.parse(design); }
    catch { return null; }
  }
  if (typeof obj !== 'object' || obj === null || !obj.ROOT) return null;

  const reachable = new Set();
  const traverse = (id) => {
    if (!id || reachable.has(id) || !obj[id]) return;
    reachable.add(id);
    (obj[id].nodes || []).forEach(traverse);
    Object.values(obj[id].linkedNodes || {}).forEach(traverse);
  };
  traverse('ROOT');

  const clean = {};
  reachable.forEach((id) => {
    const node = { ...obj[id] };

    if (Array.isArray(node.nodes)) {
      node.nodes = node.nodes.filter((cid) => reachable.has(cid));
    }
    if (node.linkedNodes && typeof node.linkedNodes === 'object') {
      const fl = {};
      Object.entries(node.linkedNodes).forEach(([k, cid]) => {
        if (reachable.has(cid)) fl[k] = cid;
      });
      node.linkedNodes = fl;
    }

    const hasKids =
      (Array.isArray(node.nodes) && node.nodes.length > 0) ||
      (node.linkedNodes && Object.keys(node.linkedNodes).length > 0);
    if (hasKids && !node.isCanvas) node.isCanvas = true;

    clean[id] = node;
  });

  return clean;
}

// ---------------------------------------------------------------------------
// EditorToolbar
// ---------------------------------------------------------------------------
function EditorToolbar({
  classes,
  onBack,
  onNew,
  onLoad,
  onOpenPages,
  onMenu,
  onPreview,
  onSave,
  isSaving,
  isLoading,
  isListing,
  lastSaved,
  currentTitle,
  designName,
}) {
  return (
    <AppBar position="static" elevation={0} className={classes.toolbar}>
      <Toolbar>
        <IconButton edge="start" onClick={onBack}>
          <Icons.ArrowBack />
        </IconButton>
        <Typography variant="h6" className={classes.title}>
          {designName || 'Editor Visual'}
          {currentTitle && (
            <Typography component="span" variant="body2" style={{ marginLeft: 12, color: '#888', fontWeight: 400 }}>
              — {currentTitle}
            </Typography>
          )}
        </Typography>

        {lastSaved && (
          <Typography variant="caption" style={{ marginRight: 16, color: '#666' }}>
            Guardado {moment(lastSaved).fromNow()}
          </Typography>
        )}

        <Button
          variant="outlined"
          startIcon={<Icons.AddCircleOutline />}
          className={classes.actionButton}
          onClick={onNew}
          disabled={isLoading || isSaving}
        >
          Nuevo diseño
        </Button>

        <Button
          variant="outlined"
          startIcon={
            isListing
              ? <CircularProgress size={16} style={{ color: '#333' }} />
              : <Icons.Collections />
          }
          className={classes.actionButton}
          onClick={onOpenPages}
          disabled={isLoading || isSaving || isListing}
        >
          Mis páginas
        </Button>

        <Button
          variant="outlined"
          startIcon={<Icons.Menu />}
          className={classes.actionButton}
          onClick={onMenu}
          disabled={isLoading || isSaving}
        >
          Menú
        </Button>

        <Button
          variant="outlined"
          startIcon={<Icons.CloudDownload />}
          className={classes.actionButton}
          onClick={onLoad}
          disabled={isLoading || isSaving}
        >
          Cargar
        </Button>

        <Button
          variant="outlined"
          startIcon={<Icons.Visibility />}
          className={classes.actionButton}
          onClick={onPreview}
          disabled={isSaving || isLoading}
        >
          Vista Previa
        </Button>

        <Button
          variant="contained"
          startIcon={
            isSaving
              ? <CircularProgress size={20} style={{ color: '#fff' }} />
              : <Icons.Save />
          }
          className={classes.actionButton}
          style={{ backgroundColor: '#00b31f', color: '#fff' }}
          onClick={onSave}
          disabled={isSaving || isLoading}
        >
          {isSaving ? 'Guardando…' : 'Guardar'}
        </Button>
      </Toolbar>
    </AppBar>
  );
}

// ---------------------------------------------------------------------------
// EditorCanvas
// REGLA CLAVE DE CRAFTJS:
//   - Si `initialDesign` existe  → <Frame data={json} />   (SIN hijos JSX)
//   - Si `initialDesign` es null → <Frame>hijos JSX</Frame> (SIN data prop)
// ---------------------------------------------------------------------------
function EditorCanvas({ classes, initialDesign }) {
  return (
    <Box className={classes.canvas}>
      <Paper className={classes.frame}>
        {initialDesign ? (
          <Frame data={JSON.stringify(initialDesign)} />
        ) : (
          <Frame>
            <Element
              is={Container}
              canvas
              backgroundColor="#ffffff"
              padding={20}
              flexDirection="column"
            >
              <Text text="¡Bienvenido al Editor Visual!" fontSize={32} textAlign="center" />
              <Text
                text="Arrastra componentes desde el panel izquierdo para construir tu página."
                fontSize={16}
                textAlign="center"
              />
              <Element
                is={Container}
                canvas
                backgroundColor="#f0f0f0"
                padding={20}
                flexDirection="column"
              >
                <Text text="Este es un contenedor de ejemplo" fontSize={18} />
                <CustomButton text="Botón de ejemplo" />
              </Element>
            </Element>
          </Frame>
        )}
      </Paper>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// EditorWrapper — tiene acceso al contexto CraftJS (useEditor)
// ---------------------------------------------------------------------------
function EditorWrapper({
  classes,
  initialDesign,
  currentPageId,
  currentTitle,
  currentPageType,
  currentContentType,
  currentLinkedPageId,
  designName,
  designId,
  currentMenu,
  onMenuSaved,
  onBack,
  onNew,
  saveDesign,
  loadDesign,
  listDesigns,
  deleteDesign,
  isSaving,
  isLoading,
  isListing,
  isDeleting,
  lastSaved,
  onPageSelected,
  onSaveSuccess,
  notify,
}) {
  const { query } = useEditor();

  const [openPreview,     setOpenPreview]   = useState(false);
  const [previewData,     setPreviewData]   = useState(null);
  const [openPagesDialog, setOpenPagesDialog] = useState(false);

  // ── Dialog "Nueva página" ─────────────────────────────────────────────────
  const [openMenuDialog,     setOpenMenuDialog]     = useState(false);

  const [openSaveDialog,     setOpenSaveDialog]     = useState(false);
  const [pendingJson,        setPendingJson]        = useState(null);
  const [titleInput,         setTitleInput]         = useState('');
  const [titleError,         setTitleError]         = useState('');
  const [dialogError,        setDialogError]        = useState('');
  const [pendingPageType,    setPendingPageType]     = useState('static');
  const [pendingContentType, setPendingContentType] = useState('');
  const [pendingLinkedPageId, setPendingLinkedPageId] = useState('');
  const [linkedPageOptions,  setLinkedPageOptions]  = useState([]);
  const [isLoadingLinked,    setIsLoadingLinked]    = useState(false);

  // ── Guardar ───────────────────────────────────────────────────────────────
  const handlePageTypeChange = async (newType) => {
    setPendingPageType(newType);
    setPendingContentType('');
    setPendingLinkedPageId('');
    setDialogError('');
    if (newType === 'dynamic_detail') {
      setIsLoadingLinked(true);
      const result = await listDesigns();
      if (result.success) {
        setLinkedPageOptions(result.pages.filter((p) => p.pageType === 'dynamic_list'));
      }
      setIsLoadingLinked(false);
    } else {
      setLinkedPageOptions([]);
    }
  };

  const handleSave = async () => {
    try {
      const json = query.serialize();
      if (!currentPageId) {
        setPendingJson(json);
        setTitleInput('');
        setTitleError('');
        setDialogError('');
        setPendingPageType('static');
        setPendingContentType('');
        setPendingLinkedPageId('');
        setLinkedPageOptions([]);
        setOpenSaveDialog(true);
      } else {
        await doSave(json, currentPageId, currentTitle, currentPageType, currentContentType, currentLinkedPageId);
      }
    } catch (err) {
      notify('❌ Error al preparar el guardado: ' + err.message, 'error');
    }
  };

  const handleConfirmSave = async () => {
    const trimmed = titleInput.trim();
    if (!trimmed) {
      setTitleError('El nombre es requerido');
      return;
    }
    const isDynamic = pendingPageType === 'dynamic_list' || pendingPageType === 'dynamic_detail';
    if (isDynamic && !pendingContentType) {
      setDialogError('Debes seleccionar el tipo de contenido');
      return;
    }
    if (pendingPageType === 'dynamic_detail' && !pendingLinkedPageId) {
      setDialogError('Debes seleccionar la página de listado asociada');
      return;
    }
    setOpenSaveDialog(false);
    await doSave(pendingJson, null, trimmed, pendingPageType, pendingContentType || null, pendingLinkedPageId || null);
  };

  const doSave = async (json, pageId, title, pageType = 'static', contentType = null, linkedPageId = null) => {
    try {
      const result = await saveDesign(json, {}, pageId, title, pageType, contentType, linkedPageId);
      if (result.success) {
        onSaveSuccess(result.data?.pageId || pageId, title, pageType, contentType, linkedPageId);
        notify('✅ Diseño guardado exitosamente', 'success');
      } else {
        throw new Error(result.error || 'Error al guardar');
      }
    } catch (err) {
      notify('❌ Error al guardar el diseño: ' + err.message, 'error');
    }
  };

  // ── Cargar ────────────────────────────────────────────────────────────────
  const handleLoad = useCallback(async () => {
    try {
      const result = await loadDesign();
      if (result.success && result.design) {
        notify('✅ Diseño disponible. Recarga la página para aplicarlo.', 'info');
      } else {
        notify('ℹ️ No hay diseño guardado.', 'info');
      }
    } catch (err) {
      notify('⚠️ Error al verificar diseño: ' + err.message, 'warning');
    }
  }, [loadDesign, notify]);

  // ── Vista previa ──────────────────────────────────────────────────────────
  const handlePreview = () => {
    try {
      const serialized = query.serialize();
      const clean = sanitizeDesign(serialized);
      if (!clean) throw new Error('Estructura de diseño inválida');
      setPreviewData(JSON.stringify(clean));
      setOpenPreview(true);
    } catch (err) {
      notify('❌ Error al generar vista previa: ' + err.message, 'error');
    }
  };

  // ── Seleccionar página desde diálogo ──────────────────────────────────────
  const handleSelectPage = useCallback(async (page) => {
    setOpenPagesDialog(false);
    notify(`⏳ Cargando "${page.title || page.slug}"…`, 'info');
    const result = await loadDesign(page.pageId);
    if (result.success) {
      onPageSelected(page, result.design);
    } else {
      notify('❌ No se pudo cargar el diseño: ' + (result.error || 'Error desconocido'), 'error');
    }
  }, [loadDesign, onPageSelected, notify]);

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <>
      <EditorToolbar
        classes={classes}
        onBack={onBack}
        onNew={onNew}
        onLoad={handleLoad}
        onOpenPages={() => setOpenPagesDialog(true)}
        onMenu={() => setOpenMenuDialog(true)}
        onPreview={handlePreview}
        onSave={handleSave}
        isSaving={isSaving}
        isLoading={isLoading}
        isListing={isListing}
        lastSaved={lastSaved}
        currentTitle={currentTitle}
        designName={designName}
      />

      <Grid container spacing={0}>
        <Grid item xs={2}>
          <Box className={classes.leftPanel}>
            <Toolbox pageType={currentPageType} contentType={currentContentType} />
          </Box>
        </Grid>
        <Grid item xs={8}>
          <EditorCanvas classes={classes} initialDesign={initialDesign} />
        </Grid>
        <Grid item xs={2}>
          <Box className={classes.rightPanel}><SettingsPanel /></Box>
        </Grid>
      </Grid>

      {/* Diálogo lista de páginas */}
      <DesignListDialog
        open={openPagesDialog}
        onClose={() => setOpenPagesDialog(false)}
        onSelect={handleSelectPage}
        listDesigns={listDesigns}
        deleteDesign={deleteDesign}
        isListing={isListing}
        isDeleting={isDeleting}
      />

      {/* Dialog: Nueva página (primer guardado) */}
      <Dialog
        open={openSaveDialog}
        onClose={() => setOpenSaveDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Nueva página</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" style={{ marginBottom: 16 }}>
            Configura el nombre y tipo de esta página.
          </Typography>

          <TextField
            autoFocus
            fullWidth
            variant="outlined"
            label="Nombre"
            placeholder="Ej: Inicio, Noticias, Catálogo…"
            value={titleInput}
            onChange={(e) => { setTitleInput(e.target.value); setTitleError(''); }}
            error={!!titleError}
            helperText={titleError}
            inputProps={{ maxLength: 80 }}
            onKeyPress={(e) => { if (e.key === 'Enter') handleConfirmSave(); }}
            style={{ marginBottom: 16 }}
          />

          <FormControl variant="outlined" fullWidth style={{ marginBottom: 16 }}>
            <InputLabel>Tipo de página</InputLabel>
            <Select
              value={pendingPageType}
              onChange={(e) => handlePageTypeChange(e.target.value)}
              label="Tipo de página"
            >
              <MenuItem value="static">Página fija</MenuItem>
              <MenuItem value="dynamic_list">Listado dinámico</MenuItem>
              <MenuItem value="dynamic_detail">Detalle dinámico</MenuItem>
            </Select>
            <FormHelperText>
              {pendingPageType === 'static'         && 'El contenido está directamente en el diseño.'}
              {pendingPageType === 'dynamic_list'   && 'Muestra una lista de ítems desde la base de datos.'}
              {pendingPageType === 'dynamic_detail' && 'Muestra el detalle de un ítem desde la base de datos.'}
            </FormHelperText>
          </FormControl>

          {(pendingPageType === 'dynamic_list' || pendingPageType === 'dynamic_detail') && (
            <FormControl variant="outlined" fullWidth style={{ marginBottom: 16 }}>
              <InputLabel>Tipo de contenido</InputLabel>
              <Select
                value={pendingContentType}
                onChange={(e) => { setPendingContentType(e.target.value); setDialogError(''); }}
                label="Tipo de contenido"
              >
                {CONTENT_TYPES.map((ct) => (
                  <MenuItem key={ct.value} value={ct.value}>
                    {ct.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {pendingPageType === 'dynamic_detail' && (
            <FormControl variant="outlined" fullWidth style={{ marginBottom: 8 }}>
              <InputLabel>Página de listado asociada</InputLabel>
              <Select
                value={pendingLinkedPageId}
                onChange={(e) => { setPendingLinkedPageId(e.target.value); setDialogError(''); }}
                label="Página de listado asociada"
                disabled={isLoadingLinked}
              >
                {isLoadingLinked && (
                  <MenuItem value="" disabled>Cargando páginas…</MenuItem>
                )}
                {!isLoadingLinked && linkedPageOptions.length === 0 && (
                  <MenuItem value="" disabled>No hay páginas de listado disponibles</MenuItem>
                )}
                {linkedPageOptions.map((p) => (
                  <MenuItem key={p.pageId} value={p.pageId}>
                    {p.title || p.slug}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>
                Selecciona la página de listado que corresponde a este detalle.
              </FormHelperText>
            </FormControl>
          )}

          {dialogError && (
            <Alert severity="error" style={{ marginTop: 8 }}>{dialogError}</Alert>
          )}
        </DialogContent>
        <DialogActions style={{ padding: '8px 24px 16px' }}>
          <Button onClick={() => setOpenSaveDialog(false)}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            style={{ backgroundColor: '#00b31f', color: '#fff' }}
            onClick={handleConfirmSave}
            disabled={isSaving}
            startIcon={isSaving ? <CircularProgress size={16} style={{ color: '#fff' }} /> : <Icons.Save />}
          >
            Guardar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Menú */}
      <MenuEditorDialog
        open={openMenuDialog}
        onClose={(savedMenu) => {
          setOpenMenuDialog(false);
          if (savedMenu !== null) {
            onMenuSaved(savedMenu);
            notify('Menú guardado exitosamente', 'success');
          }
        }}
        designId={designId}
        initialMenu={currentMenu}
        listDesigns={listDesigns}
      />

      {/* Modal Vista Previa */}
      <Dialog open={openPreview} onClose={() => setOpenPreview(false)} maxWidth="lg" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Vista Previa del Diseño</Typography>
            <IconButton onClick={() => setOpenPreview(false)} size="small">
              <Icons.Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers className={classes.previewContent}>
          {previewData && (
            <Paper elevation={0} style={{ backgroundColor: '#fff', padding: 20, minHeight: 500 }}>
              <Editor resolver={RESOLVER} enabled={false}>
                <Frame data={previewData} />
              </Editor>
            </Paper>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

// ---------------------------------------------------------------------------
// EditorPage — componente raíz
// ---------------------------------------------------------------------------
export default function EditorPage() {
  const classes     = useStyles();
  const history     = useHistory();
  const { designId }  = useParams();
  const finalDesignId = designId || '12345jjfjfjfj';

  const {
    saveDesign, loadDesign, listDesigns, deleteDesign,
    isSaving, isLoading, isListing, isDeleting,
    lastSaved, error, clearError,
  } = useDesignManager(finalDesignId);

  /**
   * undefined  → todavía cargando (no montar Editor aún)
   * null       → sin diseño guardado (Frame usará hijos JSX)
   * object     → diseño listo para Frame data prop
   */
  const [initialDesign,       setInitialDesign]       = useState(undefined);
  const [editorKey,           setEditorKey]           = useState(0);
  const [currentPageId,       setCurrentPageId]       = useState(null);
  const [currentTitle,        setCurrentTitle]        = useState('');
  const [currentPageType,     setCurrentPageType]     = useState('static');
  const [currentContentType,  setCurrentContentType]  = useState('');
  const [currentLinkedPageId, setCurrentLinkedPageId] = useState('');
  const [designName,          setDesignName]          = useState('');
  const [currentMenu,         setCurrentMenu]         = useState([]);

  const [openSnackbar,     setOpenSnackbar]     = useState(false);
  const [snackbarMessage,  setSnackbarMessage]  = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');

  const notify = useCallback((message, severity = 'success') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setOpenSnackbar(true);
  }, []);

  useEffect(() => {
    if (error) { notify(error, 'error'); clearError(); }
  }, [error, clearError, notify]);

  // ── Carga nombre del diseño desde WEB_DESIGN ──────────────────────────────
  useEffect(() => {
    if (!designId) return;
    let cancelled = false;
    async function fetchDesignName() {
      try {
        const { invokeApig } = await import('../libs/awsLib');
        const response = await invokeApig({
          path:   '/design/find',
          method: 'POST',
          body:   { designId },
        });
        if (cancelled) return;
        if (response.Status === 0 || response.statusCode === 200) {
          if (response.design?.name) setDesignName(response.design.name);
          if (Array.isArray(response.design?.menu)) setCurrentMenu(response.design.menu);
        }
      } catch (err) {
        // No crítico — el editor funciona igual sin el nombre
      }
    }
    fetchDesignName();
    return () => { cancelled = true; };
  }, [designId]);

  // ── Carga inicial del diseño ANTES de montar el Editor ────────────────────
  useEffect(() => {
    let cancelled = false;

    async function fetchInitialDesign() {
      try {
        const result = await loadDesign();
        if (cancelled) return;

        if (result.success && result.design) {
          const clean = sanitizeDesign(result.design);
          if (clean) {
            setInitialDesign(clean);
            if (result.pageId)          setCurrentPageId(result.pageId);
            if (result.metadata?.title) setCurrentTitle(result.metadata.title);
            if (result.pageInfo) {
              setCurrentPageType(result.pageInfo.pageType || 'static');
              setCurrentContentType(result.pageInfo.contentType || '');
              setCurrentLinkedPageId(result.pageInfo.linkedPageId || '');
            }
          } else {
            console.warn('Diseño inválido tras sanitización, usando estado inicial.');
            setInitialDesign(null);
            notify('⚠️ El diseño guardado no pudo procesarse. Se usará el estado inicial.', 'warning');
          }
        } else {
          setInitialDesign(null);
        }
      } catch (err) {
        if (cancelled) return;
        console.error('Error cargando diseño inicial:', err);
        setInitialDesign(null);
        notify('⚠️ No se pudo cargar el diseño: ' + err.message, 'warning');
      }
    }

    fetchInitialDesign();
    return () => { cancelled = true; };
  }, [finalDesignId]);

  // ── Cargar una página desde el diálogo ───────────────────────────────────
  const handlePageSelected = useCallback((page, design) => {
    const clean = sanitizeDesign(design);
    setInitialDesign(clean || null);
    setEditorKey((k) => k + 1);
    setCurrentPageId(page.pageId || null);
    setCurrentTitle(page.title || '');
    setCurrentPageType(page.pageType || 'static');
    setCurrentContentType(page.contentType || '');
    setCurrentLinkedPageId(page.linkedPageId || '');
    notify(`✅ Página "${page.title || page.slug}" cargada`, 'success');
  }, [notify]);

  // ── Callback tras guardado exitoso ────────────────────────────────────────
  const handleSaveSuccess = useCallback((pageId, title, pageType, contentType, linkedPageId) => {
    if (pageId)                      setCurrentPageId(pageId);
    if (title)                       setCurrentTitle(title);
    if (pageType !== undefined)      setCurrentPageType(pageType || 'static');
    if (contentType !== undefined)   setCurrentContentType(contentType || '');
    if (linkedPageId !== undefined)  setCurrentLinkedPageId(linkedPageId || '');
  }, []);

  // ── Nuevo diseño en blanco ────────────────────────────────────────────────
  const handleNew = useCallback(() => {
    if (window.confirm('¿Crear un nuevo diseño en blanco? Los cambios no guardados se perderán.')) {
      setInitialDesign(null);
      setEditorKey((k) => k + 1);
      setCurrentPageId(null);
      setCurrentTitle('');
      setCurrentPageType('static');
      setCurrentContentType('');
      setCurrentLinkedPageId('');
      notify('✅ Nuevo diseño en blanco creado', 'success');
    }
  }, [notify]);

  const handleGoBack = () => {
    if (window.confirm('¿Seguro que quieres salir? Asegúrate de haber guardado tus cambios.')) {
      history.push('/designs');
    }
  };

  // ── Spinner mientras se carga el diseño inicial ───────────────────────────
  if (initialDesign === undefined) {
    return (
      <Box className={classes.loadingContainer}>
        <CircularProgress size={60} style={{ color: '#00b31f' }} />
        <Typography variant="h6" style={{ marginTop: 16 }}>
          Cargando diseño…
        </Typography>
      </Box>
    );
  }

  return (
    <Box className={classes.root}>
      <Editor key={editorKey} resolver={RESOLVER}>
        <EditorWrapper
          classes={classes}
          initialDesign={initialDesign}
          currentPageId={currentPageId}
          currentTitle={currentTitle}
          currentPageType={currentPageType}
          currentContentType={currentContentType}
          currentLinkedPageId={currentLinkedPageId}
          designName={designName}
          designId={finalDesignId}
          currentMenu={currentMenu}
          onMenuSaved={setCurrentMenu}
          onBack={handleGoBack}
          onNew={handleNew}
          saveDesign={saveDesign}
          loadDesign={loadDesign}
          listDesigns={listDesigns}
          deleteDesign={deleteDesign}
          isSaving={isSaving}
          isLoading={isLoading}
          isListing={isListing}
          isDeleting={isDeleting}
          lastSaved={lastSaved}
          onPageSelected={handlePageSelected}
          onSaveSuccess={handleSaveSuccess}
          notify={notify}
        />
      </Editor>

      {(isSaving || isLoading) && (
        <Chip
          icon={isSaving ? <Icons.CloudUpload /> : <Icons.CloudDownload />}
          label={isSaving ? 'Guardando diseño…' : 'Cargando diseño…'}
          color="primary"
          className={classes.statusChip}
        />
      )}

      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setOpenSnackbar(false)}
          severity={snackbarSeverity}
          variant="filled"
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
}